import { colorStopToString, fixTransparentColorStops } from '../color-utils.js';

/**
 * Render a repeating conic gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderRepeatingConicGradient(context, gradientDef, { x, y, w, h }) {
  // Parse position from orientation
  const position = gradientDef.orientation?.position;
  let centerX = w * 0.5;
  let centerY = h * 0.5;

  if (position) {
    if (position.x?.type === '%') {
      centerX = w * (parseFloat(position.x.value) / 100);
    }
    if (position.y?.type === '%') {
      centerY = h * (parseFloat(position.y.value) / 100);
    }
  }

  // Parse starting angle
  let startAngle = 0;
  if (gradientDef.orientation?.value) {
    startAngle = parseFloat(gradientDef.orientation.value);
    startAngle = ((startAngle % 360) + 360) % 360;
    startAngle = (startAngle * Math.PI) / 180 - Math.PI / 2;
  }

  const gradient = context.createConicGradient(startAngle, x + centerX, y + centerY);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Calculate pattern length from color stops (in degrees, normalized to 0-1)
  // Priority: use deg values if available, otherwise use % values
  let patternLengthDeg = 0;
  let patternLengthPercent = 0;
  let hasDegValues = false;

  colorStops.forEach((stop) => {
    if (stop.length) {
      if (stop.length.type === 'deg') {
        hasDegValues = true;
        patternLengthDeg = Math.max(patternLengthDeg, parseFloat(stop.length.value));
      } else if (stop.length.type === '%') {
        patternLengthPercent = Math.max(patternLengthPercent, parseFloat(stop.length.value));
      }
    }
  });

  // Determine the actual pattern length (normalized to 0-1 where 1 = 360 degrees)
  let patternLength;
  if (hasDegValues && patternLengthDeg > 0) {
    patternLength = patternLengthDeg / 360;
  } else if (patternLengthPercent > 0) {
    patternLength = patternLengthPercent / 100;
  } else {
    // No explicit pattern length, use the full circle (no repeating)
    patternLength = 1;
  }

  // Avoid division by zero or infinite loops
  if (patternLength <= 0) {
    patternLength = 1;
  }

  // Calculate number of repetitions
  const repetitions = Math.ceil(1 / patternLength);

  // Add repeated color stops
  for (let i = 0; i < repetitions; i++) {
    const patternOffset = i * patternLength;

    colorStops.forEach((stop, index) => {
      let stopPositionInPattern = 0;

      if (stop.length) {
        if (stop.length.type === 'deg') {
          stopPositionInPattern = parseFloat(stop.length.value) / 360;
        } else if (stop.length.type === '%') {
          // For repeating gradients with deg-based pattern length,
          // % values are relative to the pattern length
          if (hasDegValues) {
            stopPositionInPattern = (parseFloat(stop.length.value) / 100) * patternLength;
          } else {
            // Pure percentage-based repeating gradient
            stopPositionInPattern = parseFloat(stop.length.value) / 100;
          }
        }
      } else {
        // No length specified, distribute evenly within the pattern
        stopPositionInPattern = (index / (colorStops.length - 1)) * patternLength;
      }

      const position = patternOffset + stopPositionInPattern;
      const colorValue = colorStopToString(stop);

      if (colorValue && position >= 0 && position <= 1) {
        gradient.addColorStop(Math.max(0, Math.min(1, position)), colorValue);
      }
    });
  }

  return gradient;
}
